import { activeSiteConfig } from "@config/site/site-config";
import { useStoreAvailability } from "@features/stores/hooks/useStoreAvailability";
import {
    MAP_TAB_SWITCH_LIST,
    MAP_TAB_SWITCH_MAP,
} from "@e2e/helpers/store-locator/constants/common";
import type { LineItem } from "@graphql/generated/types";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Button, ButtonGroup } from "@ecco/ui/button";
import { AspectRatio } from "@ecco/ui/aspect-ratio";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { Grid, GridItem } from "@ecco/ui/grid";
import { HStack } from "@ecco/ui/stack";
import { Text } from "@ecco/ui/text";
import { scrollbarStyles } from "@ecco/ui/css/scrollbar";
import { use, useState } from "react";
import { Capture } from "./capture";
import { CardItem } from "./CardItem";
import { CardItems } from "./CardItems";
import type { StoreData } from "@features/stores/types";
import {
    type StoreLocatorLocation,
    type StoreLocatorSearchControl,
    Controller,
    Provider,
} from "./controller";
import { FilterDropdowns } from "./FilterDropdowns";
import { filterValueStoreType, getDistanceFilter } from "./filterValues";
import { StoreCount } from "./StoreCount";
import { StoreList } from "./StoreList";
import { StoreLocatorMap } from "./StoreLocatorMap";
import { StoreModal } from "./StoreModal";
import { ShippingMethodTypes } from "@features/checkout/types";
import type { ProductVariant } from "@lib/product/types";
import { use2xlBreakpoint, useLgBreakpoint, useXlBreakpoint } from "@ecco/ui/use-breakpoint";
import { AutocompleteInput } from "./components/AutocompleteInput/AutocompleteInput";
import type { JSX, ReactNode } from "react";

interface StoreLocatorProps {
    storesWithStock?: StoreData[];
    selectedProductData?: any | LineItem | LineItem[];
    selectedVariant?: ProductVariant;
    onClose?: () => void;
    onStoreSelected?: (store: StoreData) => void;
    searchControl?: StoreLocatorSearchControl;
    searchResults?: { map?: StoreData[]; location?: StoreData[] };
    shippingMethodKey?: ShippingMethodTypes;
    showStoreBadge?: boolean;
    renderStoreConfirmation?: (store?: StoreData) => ReactNode;
    isPickUpSelection?: boolean;
    location?: StoreLocatorLocation;
}

export const StoreLocator = (props: StoreLocatorProps): JSX.Element => {
    const getStoresWithAvailability = useStoreAvailability(
        props.storesWithStock,
        props.shippingMethodKey !== ShippingMethodTypes.PARCEL_SHOP
    );
    return (
        <Provider
            storeLocatorLocation={props.location}
            initStores={props.storesWithStock}
            getStoresWithAvailability={getStoresWithAvailability}
            shippingMethodKey={props.shippingMethodKey}
            showStoreBadge={props.showStoreBadge}
            onStoreSelected={props.onStoreSelected}
            searchControl={props.searchControl}
        >
            <StoreLocatorUi {...props} />
        </Provider>
    );
};

export const StoreLocatorUi = ({
    storesWithStock,
    selectedProductData,
    onClose,
    selectedVariant,
    shippingMethodKey,
    searchResults,
    renderStoreConfirmation,
    isPickUpSelection = true,
    location,
}: StoreLocatorProps): JSX.Element => {
    const [searchValue, setSearchValue] = useState("");
    const { getMultiple } = useMicrocopy();

    const isLargeViewport = useLgBreakpoint();
    const isExtraLargeViewport = useXlBreakpoint();
    const isTwoExtraLargeViewport = use2xlBreakpoint();

    const { setSearchResult, searchValidation } = use(Controller);

    // If we have a selectedVariant, that means will be StoreLocator for C&C.
    // The default behavior of the StoreLocator is to show the list view, on C&C, otherwise show the map.
    const [tab, setTab] = useState<"list" | "map">("map");

    const microcopies = getMultiple(CommonCMS.page, [
        ClickAndCollect.storeLocatorList,
        ClickAndCollect.storeLocatorMap,
        ClickAndCollect.storesWithin,
        ClickAndCollect.storeLocatorPlaceholder,
    ]);

    const isMapTab = tab === "map";
    const { country } = activeSiteConfig;

    const hideCaptureButton =
        activeSiteConfig.checkout?.parcelShops?.searchMethod === "address" &&
        shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;

    return (
        <Grid
            templateColumns={[
                `repeat(4, 1fr)`,
                `repeat(4, 1fr)`,
                `repeat(4, 1fr)`,
                `repeat(12, 1fr)`,
            ]}
            flex={1}
            h={{ base: isMapTab ? "full" : "calc(100vh - 65px)" }}
        >
            <GridItem
                gridRow={[1]}
                colStart={[1]}
                colSpan={{ base: 4 }}
                sx={{
                    ...(isExtraLargeViewport && { contain: "size" }),
                    overflow: "hidden",
                }}
            >
                <Flex
                    direction="column"
                    align="stretch"
                    maxHeight="100%"
                    pr={{ base: 0, lg: 3 }}
                    h={[
                        // TODO: Refactor this to make adapt all the mediaqueries in a responsive way.
                        "full",
                    ]}
                >
                    {isPickUpSelection &&
                        (selectedProductData && selectedProductData?.length >= 1 ? (
                            <Box mb={6}>
                                <CardItems selectedProductData={selectedProductData} />
                            </Box>
                        ) : (
                            <Box mb={6}>
                                <CardItem selectedProductData={selectedProductData} />
                            </Box>
                        ))}
                    <AutocompleteInput
                        placeholder={microcopies[ClickAndCollect.storeLocatorPlaceholder]}
                        onSelect={(place) => setSearchResult(place)}
                        value={searchValue}
                        onChange={setSearchValue}
                        location={location}
                    />
                    {searchValidation.isRestricted && (
                        <Text color="red.500" fontSize="desktopBodyB3" pt={2} role="alert">
                            {searchValidation.message}
                        </Text>
                    )}
                    {/* <Search isPickUpSelection={isPickUpSelection} /> */}
                    {!hideCaptureButton && (
                        <HStack justifyContent="space-between" pt={3} pb={4}>
                            <Capture searchResults={searchResults?.location} />
                        </HStack>
                    )}
                    {!isPickUpSelection && (
                        <>
                            <Flex mx={[3, 0]} gap={"0.2"} mb={4}>
                                <FilterDropdowns
                                    // If the country is US or JP, we show the US/JP filter with different values, otherwise we show the CA/Default filter values with km/english
                                    filter={getDistanceFilter(country)}
                                />
                                <FilterDropdowns filter={filterValueStoreType} />
                            </Flex>
                            {isExtraLargeViewport && (
                                <StoreCount microcopy={microcopies[ClickAndCollect.storesWithin]} />
                            )}
                        </>
                    )}
                    <Flex alignItems={"center"} mb={4} justifyContent={"space-between"}>
                        {!isLargeViewport && (
                            <>
                                <StoreCount
                                    isExtraLargeViewport={isExtraLargeViewport}
                                    microcopy={microcopies[ClickAndCollect.storesWithin]}
                                />
                                <ButtonGroup spacing="px" ml={"auto"}>
                                    <Button
                                        size="xs"
                                        variant={tab === "list" ? "solid" : "tertiary"}
                                        onClick={() => setTab("list")}
                                        fontSize="mobileLinkL1"
                                        data-testid={MAP_TAB_SWITCH_LIST}
                                    >
                                        <span>{microcopies[ClickAndCollect.storeLocatorList]}</span>
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant={tab === "map" ? "solid" : "tertiary"}
                                        onClick={() => setTab("map")}
                                        fontSize="mobileLinkL1"
                                        data-testid={MAP_TAB_SWITCH_MAP}
                                    >
                                        <span>{microcopies[ClickAndCollect.storeLocatorMap]}</span>
                                    </Button>
                                </ButtonGroup>
                            </>
                        )}
                    </Flex>
                    <Flex flex={1} direction="column" overflow="hidden" display="flex">
                        <Box
                            flex={1}
                            overflow="auto"
                            sx={{
                                div: {
                                    ...scrollbarStyles,
                                },
                            }}
                            h="full"
                        >
                            <StoreList
                                selectedVariant={selectedVariant}
                                onClose={onClose}
                                renderStoreConfirmation={renderStoreConfirmation}
                                hideLoader={!isExtraLargeViewport && isMapTab}
                                useAvailabilityFallback={
                                    !isExtraLargeViewport && Boolean(storesWithStock)
                                }
                                shippingMethodKey={shippingMethodKey}
                            />
                        </Box>
                    </Flex>
                </Flex>
            </GridItem>
            <GridItem
                colStart={isMapTab ? [] : [1]}
                gridRow={isMapTab ? [] : [1]}
                colSpan={{ base: 4, lg: 8 }}
            >
                {!isExtraLargeViewport && (
                    <AspectRatio ratio="2/3" left={{ base: isMapTab ? 0 : "-200%", lg: 0 }}>
                        <StoreLocatorMap
                            // We are using this value only to determine the zoom levels for the map
                            isExtraLargeViewport={isTwoExtraLargeViewport}
                            hasStock={Boolean(storesWithStock)}
                            shippingMethodKey={shippingMethodKey}
                            searchResults={searchResults?.map}
                        />
                    </AspectRatio>
                )}
                {isExtraLargeViewport && (
                    // We need to calculate the total height of the viewport - the height of the header and the navigation - the space below the map and footer
                    <Box minHeight={"calc(100vh - 120px)"} position="relative">
                        <StoreLocatorMap
                            isExtraLargeViewport={isTwoExtraLargeViewport}
                            hasStock={Boolean(storesWithStock)}
                            shippingMethodKey={shippingMethodKey}
                            searchResults={searchResults?.map}
                        />
                    </Box>
                )}
            </GridItem>
            <StoreModal
                onClose={onClose}
                selectedVariant={selectedVariant}
                renderStoreConfirmation={renderStoreConfirmation}
                shippingMethodKey={shippingMethodKey}
            />
        </Grid>
    );
};
